class Preview extends EventHandler {
  constructor (displayable, app) {
    super();

    this._IMAGE_REGEX   = /https?:\/\/[^\s"]+\.(?:jpe?g|gif|png|svg)/
    this._PREVIEW_CLASSES = {
      withoutContent: 'ctext-wrap-preview',
      withContent: 'ctext-wrap-preview-with-content',
      image: 'ctext-wrap-preview-image',
      none: ''
    };

    this._originalDisplayable = displayable;
    this.app                  = app;

    this._previewHash         = null;
    this._ctextWrap           = null;
    this._attached            = false; // Wether the preview has been appended to the message or not yet
    this._previewClass        = 'none';
    this._displayableContent  = displayable;
    this._parseDisplayable(displayable);
  }

  get cssClass () {
    return this._PREVIEW_CLASSES[this._previewClass];
  }

  get displayableContent () {
    return this._displayableContent;
  }

  get enabled () {
    return this.displayableContent != this._originalDisplayable;
  }

  /**
   * Call this method as soon as the related message element has been added
   * to the DOM.
   * It will change the behavior of _setPreviewClass and _setDisplayableContent
   * so it alters the existing DOM element.
   * @param {boolean} attached
   */
  setAttached (attached) {
    this._attached = attached;
  }

  _getCtextWrap () {
    if (!this._attached || !this._previewHash) return null;
    if (!this._ctextWrap) {
      const previewElement = Dom.findById('widget', `slaask-link-preview-${this._previewHash}`);
      this._ctextWrap = Utils.closest(previewElement, '.ctext-wrap');
    }
    return this._ctextWrap;
  }

  /**
   * @param {string} previewClass One of the keys of this._PREVIEW_CLASSES
   */
  _setPreviewClass (previewClass) {
    this._previewClass = previewClass;
    if (this._attached) {
      const ctextWrap = this._getCtextWrap();
      if (ctextWrap) {
        Object.keys(this._PREVIEW_CLASSES).forEach(key => {
          Utils.removeClass(ctextWrap, this._PREVIEW_CLASSES[key]);
        });
        if (this._PREVIEW_CLASSES[previewClass]) Utils.addClass(ctextWrap, this._PREVIEW_CLASSES[previewClass]);
      }
      else {
        Utils.warn('Preview is declared as attached but has no ctextWrap');
      }
    }
  }

  _setDisplayableContent (content) {
    this._displayableContent = content;
    if (this._attached) {
      const ctextWrap = this._getCtextWrap();
      if (ctextWrap) {
        ctextWrap.querySelector('.slaask-message-body').innerHTML = content;
        const messageEl = Utils.closest(ctextWrap, 'li');
        if (messageEl) Conversations._decorateAiCopyLinks(messageEl);
      }
      else {
        Utils.warn('Preview is declared as attached but has no ctextWrap');
      }
    }
  }

  /**
   * Removes the preview for this message
   */
  _discardPreview () {
    this._setPreviewClass('none');
    this._setDisplayableContent(this._originalDisplayable);
  }

  _parseDisplayable (displayable) {
    let displayableElement       = document.createElement('div');
    displayableElement.innerHTML = displayable;

    const translationLabel = displayableElement.querySelector('.translated-label');
    if (translationLabel) {
      translationLabel.parentNode.removeChild(translationLabel);
      this._setDisplayableContent(displayableElement.innerHTML);
    }

    const links = displayableElement.querySelectorAll('a:not(.slaask-btn):not(.button):not(.asktech-btn)');

    if (links.length == 0) {
      this._discardPreview();
      return;
    }

    const link  = links[0];
    const href  = link.href;

    if (href.substring(0, 4) != 'http') {
      this._discardPreview();
      return;
    }

    const isLinkOnly = this.app._askTechParser.isOnlyLink(displayable);
    const isHyperLink = Previewer.isHyperLink(link);
    const isImage = href.match(this._IMAGE_REGEX);

    // Check if there's text content beyond just the link URL
    const textContent = displayableElement.textContent || '';
    const linkText = link.textContent || '';
    const hasTextBeyondLink = textContent.trim() !== linkText.trim() && textContent.trim() !== href.trim();

    if (isLinkOnly && isImage && !hasTextBeyondLink) {
      this._setDisplayableContent(`
        <a class="slaask-pointer" target="_blank" href="${href}">
          <img onload="" src="${href}"/>
        </a>`);
      this._setPreviewClass('image');
      return;
    }
    else if (isLinkOnly && !isHyperLink) {
      this._setDisplayableContent('');
      this._setPreviewClass('withoutContent');
    }
    else {
      this._setPreviewClass('withContent');
      const imageLinks = Array.prototype.filter.call(links, l => l.href && l.href.match(this._IMAGE_REGEX));
      if (imageLinks.length > 0 && hasTextBeyondLink) {
        imageLinks.forEach((imgLink) => {
          if (imgLink.innerText.trim() === 'attachment') {
            const previousSibling = imgLink.previousSibling;
            if (previousSibling && previousSibling.nodeType === Node.TEXT_NODE) {
              previousSibling.textContent = previousSibling.textContent.replace(/!\s*$/, '');
            }
          }
          const imgHref = imgLink.href;
          imgLink.outerHTML = `<a class="slaask-pointer" target="_blank" href="${imgHref}">
          <img onload="" src="${imgHref}"/>
        </a>`;
        });
        this._setDisplayableContent(displayableElement.innerHTML);
        this._setPreviewClass('none');
        return;
      }
      
      if (link.innerText == 'attachment') {
        if (isImage && !hasTextBeyondLink) {
          this._setDisplayableContent(`
            <a class="slaask-pointer" target="_blank" href="${href}">
              <img onload="" src="${href}"/>
            </a>`);
          this._setPreviewClass('image');
          return;
        } else {
          link.parentElement.removeChild(link);
          this._setDisplayableContent(displayableElement.innerHTML);
        }
      }
    }

    this._previewHash                 = Utils.generateRandomToken()
    const linkPreviewElementContainer = document.createElement('div');
    const linkPreviewElement          = document.createElement('a');
    linkPreviewElement.href           = href;
    linkPreviewElement.className      = 'slaask-link-preview';
    linkPreviewElement.id             = `slaask-link-preview-${this._previewHash}`;
    linkPreviewElement.innerHTML      = href;
    linkPreviewElement.setAttribute('target', '_blank');
    linkPreviewElementContainer.appendChild(linkPreviewElement);

    const sessionStorageKey = encodeURIComponent(href) + '-' + this.app._language;
    const translationSuffix = translationLabel ? translationLabel.outerHTML : '';
    const appendPreviewToContent = () => {
      this._setDisplayableContent(
        this.displayableContent +
        translationSuffix +
        linkPreviewElementContainer.outerHTML);
    };
    const isUnpreviewable = (data) => data.type == 'blacklisted' || data.type == 'unavailable';

    let linkData = Utils.sessionStorage(sessionStorageKey);
    if (!linkData) {
      appendPreviewToContent();
      Previewer.getPreviewData(href, this.app._language).then(linkData => {
        Utils.sessionStorage(sessionStorageKey, linkData);
        if (isUnpreviewable(linkData)) {
          this._discardPreview();
          return;
        }
        if (linkData.type == 'photo' && isLinkOnly) this._setPreviewClass('image');
        const previewElement = this._renderPreview(this._previewHash, linkData, isLinkOnly, href);
        if (previewElement) this.app.handleImageLoad(previewElement);
        else this._discardPreview();
      }).catch(() => {
        this._discardPreview();
      });
    }
    else if (isUnpreviewable(linkData)) {
      this._setPreviewClass('none');
    }
    else {
      Views.getByName('conversation').tempBlockScroll(300);
      if (linkData.type == 'photo' && isLinkOnly) this._setPreviewClass('image');
      const previewElement = this._renderPreview(linkPreviewElement, linkData, isLinkOnly, href);
      if (previewElement) appendPreviewToContent();
      else this._discardPreview();
    }
  }

  /**
   * This function may be called before or after the message has been rendered
   * @param {string|HTMLElement} hashOrElement
   * @param {Object} linkData
   * @param {boolean} isLinkOnly
   * @param {string} href
   * @returns {HTMLElement} Returns the element of the preview.
   *                        If null, the preview shouldn't be rendered.
   */
  _renderPreview (hashOrElement, linkData, isLinkOnly, href) {
    if (linkData.type == 'blacklisted' || linkData.type == 'unavailable') {
      return null;
    }

    let previewElement = typeof hashOrElement == 'string'
                          ? Dom.findById('widget', `slaask-link-preview-${hashOrElement}`)
                          : hashOrElement;
    if (previewElement) {
      let builded = this._buildPreview(linkData, href);
      if (builded.html == '') {
        if (isLinkOnly) {
          try {
            previewElement.innerHTML = `<div class="slaask-link-preview-description slaask-link-preview-description-break">${href}</div><div class="slaask-link-preview-site">${href.replace('https://', '').replace('http://', '').split('/')[0]}</div>`;
          } catch(e) {
            previewElement.innerHTML = `<div class="slaask-link-preview-description slaask-link-preview-description-break">${href}</div>`;
          }
        } else {
          return null;
        }
      } else {
        if (builded.replaceByIframe) {
          let addedDiv = document.createElement('div');
          addedDiv.id         = previewElement.id;
          addedDiv.className  = 'slaask-link-preview slaask-link-preview-iframe';
          addedDiv.innerHTML  = builded.html;
          previewElement.parentNode.replaceChild(addedDiv, previewElement);
        } else {
          previewElement.innerHTML = builded.html;
        }
      }
    }
    return previewElement;
  }

  _buildPreview (linkData, href) {
    let replaceByIframe = false
    let previewHtml   = ''
    let largePreview  = ''
    let previewImage  = ''

    // if (linkData.thumbnail_url == null && linkData.favicon) {
    //   linkData.thumbnail_url = linkData.favicon
    // }

    switch(linkData.type) {
      case 'link':
      case 'rich':
        if (this.app._large_preview_enabled && linkData.thumbnail_url) {
          largePreview = `<div class="slaask-link-preview-large-preview">
            <img src="${linkData.thumbnail_url}"/>
          </div>`
        } else {
          previewImage = linkData.favicon ? `<div class="slaask-link-preview-image">
            <img src="${linkData.favicon}"/>
          </div>` : ''
        }

        const newTabIcon = `<svg class="slaask-link-preview-large-preview-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1.4873 3.82812V14.5126L11.8115 14.5128" stroke="currentColor" stroke-width="2"/>
          <path d="M8.92383 7.22705L14.4293 1.72162" stroke="currentColor" stroke-width="2"/>
          <path d="M9.61133 1.4873H14.5129V6.53549" stroke="currentColor" stroke-width="2" stroke-linecap="square"/>
        </svg>`

        linkData.title = linkData.title.replace(` | ${linkData.provider_name}`, '')

        let previewTitle = linkData.title ? `<div class="slaask-link-preview-title${previewImage != '' ? ' slaask-link-preview-title-with-image' : ''}">
          ${linkData.title}
          ${this.app._large_preview_enabled ? newTabIcon : ''}
        </div>` : ''

        if (previewTitle != '') {
          let previewDescription = linkData.description ? `<div class="slaask-link-preview-description">
            ${linkData.description}
          </div>` : ''

          let previewSite = linkData.provider_url ? `<div class="slaask-link-preview-site">
            ${linkData.provider_url.replace('https://', '').replace('http://', '')}
          </div>` : ''


          previewHtml = largePreview + previewImage + previewTitle + previewDescription + previewSite
        }
        break;

      case 'video':
        // Special handling for YouTube: show a clickable thumbnail that opens YouTube,
        // instead of embedding the player iframe directly (avoids YouTube error 153
        // in nested iframe environments).
        if ((linkData.provider_name && linkData.provider_name.toLowerCase() === 'youtube') || href.match(/(youtube\.com|youtu\.be)/i)) {
          let videoId = null;
          try {
            // Try to extract from the URL first
            const urlMatch = href.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
            if (urlMatch && urlMatch[1]) {
              videoId = urlMatch[1];
            }
            // Fallback: attempt to parse from the oEmbed HTML if available
            if (!videoId && linkData.html) {
              const htmlMatch = linkData.html.match(/\/embed\/([A-Za-z0-9_-]{11})/);
              if (htmlMatch && htmlMatch[1]) {
                videoId = htmlMatch[1];
              }
            }
          } catch (e) {}

          if (videoId) {
            const thumbUrl = this.app._cdn_url + '/proxy/image?url=' + encodeURIComponent(`https://i.ytimg.com/vi/${videoId}/hq720.jpg`);
            previewHtml = `
              <div class="youtube-preview-container">
                <a class="slaask-pointer" href="${href}" target="_blank" rel="noopener noreferrer">
                  <img src="${thumbUrl}" alt="${linkData.title || ''}"/>
                  <button class="ytp-large-play-button" aria-label="Play YouTube video" type="button">
                    <svg height="100%" version="1.1" viewBox="0 0 68 48" width="100%">
                      <path class="ytp-large-play-button-bg" d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,0.5,34,0.5,34,0.5s-21.79,0-27.1,1.05
                        C3.97,2.33,2.27,4.81,1.48,7.74C0.44,13.05,0.44,24,0.44,24s0,10.95,1.04,16.26c0.79,2.93,2.49,5.41,5.42,6.19
                        C12.21,47.5,34,47.5,34,47.5s21.79,0,27.1-1.05c2.93-0.78,4.64-3.26,5.42-6.19C67.56,34.95,67.56,24,67.56,24
                        S67.56,13.05,66.52,7.74z"></path>
                      <path fill="#fff" d="M 45,24 27,14 27,34"></path>
                    </svg>
                  </button>
                </a>
              </div>`;
            replaceByIframe = true;
            break;
          }
        }

        // Fallback for non-YouTube (or unparsed) video providers: keep original iframe/html
        previewHtml   = linkData.html
        replaceByIframe = true
        break;

      case 'photo':
        previewHtml   = `<img onload="" src="${linkData.url || href}"/>`
        break;
    }

    return { replaceByIframe: replaceByIframe, html: previewHtml }
  }
}
